/**
 * Servizio per la gestione del calendario degli appuntamenti
 * Integrazione con RECUP API v3/v4 per bookings
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export interface BookingEvent {
  id: string;
  bookingId: string;
  bookingCode?: string;
  title: string;
  start: Date;
  end: Date;
  patientName?: string;
  patientFiscalCode?: string;
  diaryId: string;
  diaryName?: string;
  serviceCode?: string;
  serviceName?: string;
  status?: string;
  priority?: string;
  notes?: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  extendedProps?: any;
}

export interface CalendarFilters {
  diaryIds: string;
  fromDate: string;
  toDate: string;
  fiscalCode?: string;
  patientId?: string;
  view?: 'normal' | 'complete' | 'ultraverbose';
}

export interface CreateBookingDto {
  patientId: string;
  diaryId: string;
  serviceCode: string;
  timeSlot: string; // ISO 8601 datetime
  priority?: 'NORMAL' | 'URGENT';
  nre?: string; // Numero Ricetta Elettronica (obbligatorio per SSN)
  notes?: string;
}

export interface MoveBookingDto {
  bookingId: string;
  newDiaryId: string;
  newTimeSlot: string; // ISO 8601 datetime
  reason?: string;
}

export interface UpdateBookingStatusDto {
  bookingId: string;
  status: 'CANCELLED' | 'CONFIRMED' | 'MODIFIED';
  reason?: string;
}

class CalendarService {
  private getHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Recupera gli appuntamenti per il calendario
   * Usa l'endpoint GET /api/v3/system-apis/bookings
   */
  async getBookings(filters: CalendarFilters): Promise<BookingEvent[]> {
    try {
      const params: any = {
        diaryIds: filters.diaryIds,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        view: filters.view || 'ultraverbose',
        sort: '+startTime',
        range: '0-999' // Recupera molti record
      };

      if (filters.fiscalCode) {
        params.fiscalCode = filters.fiscalCode;
      }

      if (filters.patientId) {
        params.patientId = filters.patientId;
      }

      // Chiamata al backend test endpoint che gestisce la conversione
      const response = await axios.post(
        `${API_BASE_URL}/recup-test/test-endpoint`,
        {
          endpointPath: '/api/v3/system-apis/bookings',
          parameters: params,
          method: 'GET',
          endpointId: 'bookings-search-v3'
        },
        { headers: this.getHeaders() }
      );

      if (response.data.success && response.data.data) {
        // La risposta ha struttura nidificata: response.data.data.data.content
        const bookingsData = response.data.data.data?.content || response.data.data.content || response.data.data;
        return this.convertToCalendarEvents(bookingsData);
      }

      return [];
    } catch (error: any) {
      console.error('Error fetching bookings:', error);
      throw new Error(error.response?.data?.message || 'Errore nel recupero degli appuntamenti');
    }
  }

  /**
   * Recupera i dettagli di un'agenda specifica con le prestazioni associate
   * Usa l'endpoint GET /api/v3/system-apis/diaries/{diaryId}
   */
  async getDiaryDetails(diaryId: string): Promise<any> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/recup-test/test-endpoint`,
        {
          endpointPath: '/api/v3/system-apis/diaries/{diaryId}',
          parameters: {
            diaryId: diaryId,
            view: 'ultraverbose'
          },
          method: 'GET',
          endpointId: 'diary-get-v3'
        },
        { headers: this.getHeaders() }
      );

      if (response.data.success) {
        return response.data.data?.data || response.data.data;
      }

      throw new Error(response.data.error || 'Errore nel recupero dell\'agenda');
    } catch (error: any) {
      console.error('Error fetching diary details:', error);
      throw new Error(error.response?.data?.message || 'Errore nel recupero dell\'agenda');
    }
  }

  /**
   * Crea un nuovo appuntamento
   * Usa l'endpoint POST /api/v4/process-apis/booking-management/bookings
   */
  async createBooking(booking: CreateBookingDto): Promise<any> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/recup-test/test-endpoint`,
        {
          endpointPath: '/api/v4/process-apis/booking-management/bookings',
          parameters: {
            patientId: booking.patientId,
            diaryId: booking.diaryId,
            serviceCode: booking.serviceCode,
            timeSlot: booking.timeSlot,
            priority: booking.priority || 'NORMAL',
            nre: booking.nre || '',
            notes: booking.notes || ''
          },
          method: 'POST',
          endpointId: 'booking-create-v4'
        },
        { headers: this.getHeaders() }
      );

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Errore nella creazione dell\'appuntamento');
    } catch (error: any) {
      console.error('Error creating booking:', error);
      throw new Error(error.response?.data?.message || 'Errore nella creazione dell\'appuntamento');
    }
  }

  /**
   * Sposta un appuntamento esistente
   * Usa l'endpoint POST /api/v4/process-apis/booking-management/bookings/move
   */
  async moveBooking(moveData: MoveBookingDto): Promise<any> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/recup-test/test-endpoint`,
        {
          endpointPath: '/api/v4/process-apis/booking-management/bookings/move',
          parameters: {
            bookingId: moveData.bookingId,
            newDiaryId: moveData.newDiaryId,
            newTimeSlot: moveData.newTimeSlot,
            reason: moveData.reason || 'Spostamento manuale da calendario'
          },
          method: 'POST',
          endpointId: 'booking-move-v4'
        },
        { headers: this.getHeaders() }
      );

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Errore nello spostamento dell\'appuntamento');
    } catch (error: any) {
      console.error('Error moving booking:', error);
      throw new Error(error.response?.data?.message || 'Errore nello spostamento dell\'appuntamento');
    }
  }

  /**
   * Aggiorna lo stato di un appuntamento (es. cancellazione)
   * Usa l'endpoint PATCH /api/v3/process-apis/booking-management/bookings
   */
  async updateBookingStatus(updateData: UpdateBookingStatusDto): Promise<any> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/recup-test/test-endpoint`,
        {
          endpointPath: '/api/v3/process-apis/booking-management/bookings',
          parameters: {
            bookingId: updateData.bookingId,
            status: updateData.status,
            reason: updateData.reason || ''
          },
          method: 'PATCH',
          endpointId: 'booking-patch-v3'
        },
        { headers: this.getHeaders() }
      );

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Errore nell\'aggiornamento dello stato');
    } catch (error: any) {
      console.error('Error updating booking status:', error);
      throw new Error(error.response?.data?.message || 'Errore nell\'aggiornamento dello stato');
    }
  }

  /**
   * Converte i dati RECUP in eventi FullCalendar
   */
  private convertToCalendarEvents(recupData: any): BookingEvent[] {
    try {
      // Gestisce sia array che oggetto con items
      const bookings = Array.isArray(recupData) ? recupData : (recupData.items || []);

      return bookings.map((booking: any) => {
        const startTime = new Date(booking.startTime || booking.slot?.startTime);
        const endTime = new Date(booking.endTime || booking.slot?.endTime);

        // Estrai stato e priorità (possono essere oggetti o stringhe)
        const statusName = typeof booking.status === 'object' ? booking.status?.name : booking.status;
        const priorityName = typeof booking.service?.prescription?.priority === 'object'
          ? booking.service?.prescription?.priority?.name
          : booking.priority;

        // Determina il colore in base allo stato
        let backgroundColor = '#1976d2'; // Blu default
        let borderColor = '#1565c0';

        if (statusName?.toLowerCase().includes('cancel') || statusName?.toLowerCase().includes('annull')) {
          backgroundColor = '#d32f2f';
          borderColor = '#c62828';
        } else if (statusName?.toLowerCase().includes('confirm') || statusName?.toLowerCase().includes('prenotat')) {
          backgroundColor = '#388e3c';
          borderColor = '#2e7d32';
        } else if (priorityName?.toLowerCase().includes('urgent') || priorityName?.toLowerCase().includes('u ')) {
          backgroundColor = '#f57c00';
          borderColor = '#ef6c00';
        }

        return {
          id: booking.id,
          bookingId: booking.id,
          bookingCode: booking.code,
          title: this.formatEventTitle(booking),
          start: startTime,
          end: endTime,
          patientName: booking.person?.name || booking.patientName || 'Paziente',
          patientFiscalCode: booking.fiscalCode,
          diaryId: booking.diary?.id || booking.diaryId,
          diaryName: booking.diary?.name || booking.diaryName,
          serviceCode: booking.service?.code || booking.serviceCode,
          serviceName: booking.service?.name || booking.serviceName,
          status: statusName,
          priority: priorityName,
          notes: booking.patientNotes || booking.notes,
          backgroundColor,
          borderColor,
          textColor: '#ffffff',
          extendedProps: booking // Conserva tutti i dati originali
        };
      });
    } catch (error) {
      console.error('Error converting bookings to calendar events:', error);
      return [];
    }
  }

  /**
   * Formatta il titolo dell'evento per il calendario (molto compatto)
   * I nomi in RECUP sono nel formato "COGNOME NOME" (es: "ANASTASIO CIRO")
   */
  private formatEventTitle(booking: any): string {
    const patientName = booking.person?.name || booking.patient?.name || booking.patientName || 'Paziente';

    // Estrai cognome completo e prima lettera del nome (es: "ANASTASIO C.")
    const nameParts = patientName.trim().split(' ');
    let compactName = '';

    if (nameParts.length >= 2) {
      // In RECUP: primo elemento = COGNOME (intero), secondo elemento = NOME (prima lettera)
      const surname = nameParts[0]; // Cognome completo
      const firstNameInitial = nameParts[nameParts.length - 1].charAt(0); // Prima lettera dell'ultimo elemento (nome)
      compactName = `${surname} ${firstNameInitial}.`;
    } else {
      compactName = nameParts[0];
    }

    return compactName;
  }

  /**
   * Formatta una data in formato ISO 8601 per RECUP API
   */
  formatDateForApi(date: Date): string {
    return date.toISOString();
  }

  /**
   * Formatta una data in formato YYYY-MM-DD per filtri
   */
  formatDateForFilter(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

export default new CalendarService();
